import React, {forwardRef, useEffect, useMemo, useRef, useState, useCallback} from 'react'
import parse from 'html-react-parser'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import SezzleWidget from '@sezzle/sezzle-react-widget'

import {
    Flex,
    Heading,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    HStack,
    Fade,
    useMultiStyleConfig,
    useBreakpointValue,
    Badge
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Skeleton as ImageGallerySkeleton} from '@salesforce/retail-react-app/app/components/image-gallery'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '../../hooks/use-current-customer.js'
import {useContentAsset} from '../../hooks/use-content-asset'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {useWishList} from '../../hooks/use-wish-list'
import useIsMobile from '../../hooks/use-is-mobile'
import {useStickyScroll} from '../../hooks/use-sticky-scroll.js'
import {useCustomerGroups} from '../../hooks/use-customer-groups'
import {useCurrentBasket} from '../../hooks/use-current-basket.js'
import {useMiniCartContext} from '../../hooks/use-mini-cart.js'
import {useCarouselObserver} from '../../hooks/use-carousel-observer.js'
import useProductSashes from '../../hooks/use-product-sash.js'
import {useCustomObject} from '../../hooks/use-custom-object'
import {useWishlistActions} from '../../hooks/use-wishlist-action'

import SwatchGroup from '../swatch-group'
import Swatch from '../swatch-group/swatch'
import ImageGallery from '../image-gallery/index'
import Breadcrumb from '../breadcrumb'
import DisplayPrice from '../../components/display-price'
import QuantityPicker from '../quantity-picker'
import PremiumBanner from './premiumBanner'
import ShowPromotionsList from './promotions-list'
import InformationAccordion from '../../pages/product-detail/partials/information-accordion'
import ReviewsAccordion from '../../pages/product-detail/partials/reviews-accordion'
import ProductDescription from '../../pages/product-detail/partials/product-description'
import ProductFeature from '../../pages/product-detail/partials/product-feature'
import SocialSharingIcons from './social-sharing-icons.jsx'
import BackInStock from './backInStock'
import BundleChildCarousel from './bundle-child-products-carousel.jsx'
import LastPurchased from './last-purchased'
import StripePDPExpressCta from '../stripe/stripe-pdp-express-cta'
import productView from '../../theme/components/project/product-view'
import WishlistDropdownButton from '../wishlist-dropdown-button'

import {HeartIconBlack, HeartIconRed} from '../../components/icons'
import {getCookieId, isCustomerInProductGroups} from '../../utils/custom-utils'
import {
    getPriceData,
    canAddToCart,
    getCartButtonText,
    getMaxOrderQuantity
} from '../../utils/product-utils'
import {formattedDate} from '../../utils/custom-utils.js'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {carouselDataComponentCount} from '../../utils/panel-selection-utils'
import {categoryUrlBuilder} from '../../utils/url'
import {setSessionJSONItem, buildRedirectURI} from '../../utils/utils'
import {
    API_ERROR_MESSAGE,
    TOAST_MESSAGE_ADDED_TO_CART,
    PREMIUM_URL,
    PLACEHOLDER_DATE
} from '../../constants'

const ProductViewHeader = ({
    name,
    priceData,
    product,
    isProductPartOfBundle,
    brandListCustomObj,
    sashes,
    customer,
    onSocialLoginClick,
    ssoEnable
}) => {
    const configs = getConfig()
    const {currency: activeCurrency} = useCurrency()
    const productStyles = useMultiStyleConfig('ProductView')
    // Power Reviews
    const PowerReviewProductPage = useMultiStyleConfig('PowerReviewProductPage')
    // hide no 0 review and stars and show only link
    const inlinePowerReviewOverride = {
        '.p-w-r .pr-no-reviews': {
            display: 'block'
        },
        '.p-w-r .pr-no-reviews .pr-snippet-review-count, .p-w-r .pr-no-reviews .pr-snippet-stars-container':
            {display: 'none'}
    }
    const sezzleMerchantID = process.env.SEZZLE_MERCHANT_ID
    const lowStockThresholdGlobal = configs.app.library.globalLowstockThreshold
    const inventory = product?.inventory?.ats
    const isLowStockWarning =
        inventory &&
        (product?.c_lowStockThreshold
            ? inventory < product?.c_lowStockThreshold
            : inventory < lowStockThresholdGlobal)
    const ssoCookie =
        typeof window !== 'undefined' &&
        process.env.SOFT_LOGIN_COOKIE &&
        getCookieId(process.env.SOFT_LOGIN_COOKIE)
    const cookieObj = ssoCookie && JSON.parse(ssoCookie)
    const exclusiveCustomStyles = {
        '.exclusive::before': {
            content: '"."',
            display: 'block',
            backgroundImage: `url(${getAssetUrl('static/img/cr-mark-16x16.svg')})`,
            backgroundRepeat: 'no-repeat',
            float: 'left',
            width: '16px',
            paddingRight: '4px'
        }
    }
    const styles = useMultiStyleConfig('ProductSashes')

    const powerRatingImageStyles = {
        '.p-w-r .pr-star-v4-100-filled': {
            backgroundImage: `url(${getAssetUrl('static/img/rating-100.svg')})`
        },
        '.p-w-r .pr-star-v4-75-filled': {
            backgroundImage: `url(${getAssetUrl('static/img/rating-0.75.svg')})`
        },
        '.p-w-r .pr-star-v4-50-filled': {
            backgroundImage: `url(${getAssetUrl('static/img/rating-0.5.svg')})`
        },
        '.p-w-r .pr-star-v4-25-filled': {
            backgroundImage: `url(${getAssetUrl('static/img/rating-0.25.svg')})`
        },
        '.p-w-r .pr-star-v4-0-filled': {
            backgroundImage: `url(${getAssetUrl('static/img/rating-0.svg')})`
        }
    }
    const productPromotions =
        product?.c_updatePromotions?.filter((promo) => promo.promoClass === 'PRODUCT') || []

    return (
        <VStack spacing={2} align="flex-start" className="product-glace-section">
            {/* Show Sashes like EXCLUSIVE and COMING SOON above the title */}
            {sashes?.length > 0 && (
                <Box className="productsashes" mt={{base: '6px', sm: '0'}}>
                    {sashes.map((sash, index) => {
                        const isExclusive = sash.name === 'sale' && sash.msg === 'EXCLUSIVE'
                        const isComingSoon = sash.name === 'comingsoon'

                        if (!isExclusive && !isComingSoon) return null

                        if (isExclusive) {
                            return (
                                <Badge key={index} {...styles.exclusive} borderRadius={'full'}>
                                    <Box className={`sash ${sash.name}`}>
                                        <Box sx={exclusiveCustomStyles}>
                                            <Box className="exclusive">EXCLUSIVE</Box>
                                        </Box>
                                    </Box>
                                </Badge>
                            )
                        }
                        return null
                    })}
                </Box>
            )}

            {/* Title */}
            <Skeleton isLoaded={name}>
                <Heading
                    data-testid={'product-name'}
                    {...productStyles.productTitle}
                >{`${name}`}</Heading>
            </Skeleton>
            {brandListCustomObj?.response && (
                <Box data-testid="shop-all-link" {...productStyles.shopSeriesLink}>
                    {/*  <Image
                        src={brandListCustomObj?.response?.logoUrl}
                        alt="Series Logo"
                        display={{base: 'none', sm: 'flex'}}
                    /> */}
                    <Text
                        data-testid="title-logo"
                        as="p"
                        m={0}
                        lineHeight="16.8px"
                        fontWeight="medium"
                    >
                        <FormattedMessage id="shop_all_link" defaultMessage="Shop All" />
                    </Text>
                    <Link
                        href={categoryUrlBuilder({id: brandListCustomObj.response?.ID})}
                        textDecoration="underline"
                        textUnderlineOffset="2px"
                        lineHeight="16.8px"
                        fontWeight="medium"
                    >
                        {brandListCustomObj?.response?.name}
                    </Link>
                </Box>
            )}

            {/* Power Reviews */}
            <Box
                width="100%"
                sx={{
                    ...PowerReviewProductPage,
                    ...inlinePowerReviewOverride,
                    ...powerRatingImageStyles
                }}
            >
                <Box id="pr-reviewsnippet"></Box>
            </Box>

            {/* Price Section */}
            {!isProductPartOfBundle && (
                <Skeleton isLoaded={typeof priceData?.currentPrice === 'number'}>
                    {typeof priceData?.currentPrice === 'number' && (
                        <Box mt="4" mb="2">
                            <DisplayPrice
                                priceData={priceData}
                                currency={activeCurrency}
                                promotions={product?.c_updatePromotions}
                            />
                            <Box data-testid="price-details-copy" mt="2">
                                {productPromotions?.length === 1 &&
                                    productPromotions?.[0].calloutMsg && (
                                        <Box fontWeight="extrabold" lineHeight={'17px'} pb={'2px'}>
                                            {parse(productPromotions?.[0].calloutMsg)}
                                        </Box>
                                    )}
                                {productPromotions?.length > 1 && (
                                    <ShowPromotionsList productPromotions={productPromotions} />
                                )}
                                {cookieObj?.tier && customer?.isGuest && (
                                    <Text fontSize={'sm'} lineHeight={'17px'} pb={'2px'}>
                                        <FormattedMessage
                                            defaultMessage="Member Price Available"
                                            id="member_price_available"
                                        />
                                        <Link
                                            textDecoration="underline"
                                            href={ssoEnable ? '#' : '/login'}
                                            onClick={() => {
                                                if (ssoEnable) onSocialLoginClick()
                                            }}
                                        >
                                            Log In
                                        </Link>
                                    </Text>
                                )}
                                <Text fontSize={'sm'} lineHeight={'17px'} pb={'2px'}>
                                    {product?.inventory?.backorderable &&
                                        product.inventory?.stockLevel == 0 &&
                                        product?.inventory?.ats > 0 &&
                                        product?.inventory?.inStockDate &&
                                        (() => {
                                            const inStockDate = new Date(
                                                product.inventory.inStockDate
                                            )

                                            const placeholderDate = new Date(PLACEHOLDER_DATE)

                                            const isPlaceholderDate =
                                                inStockDate.toDateString() ===
                                                placeholderDate.toDateString()

                                            return isPlaceholderDate ? (
                                                <FormattedMessage
                                                    defaultMessage="Backorder date to be announced"
                                                    id="productstate_backorder"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Release date: {date}"
                                                    id="productstate_backorder_release"
                                                    values={{
                                                        date: formattedDate(inStockDate)
                                                    }}
                                                />
                                            )
                                        })()}
                                </Text>
                                <Text fontSize={'sm'} lineHeight={'17px'} pb={'2px'}>
                                    {product?.inventory?.preorderable === true &&
                                        product?.inventory?.ats > 0 &&
                                        product?.inventory?.inStockDate && (
                                            <FormattedMessage
                                                defaultMessage="Preorder expected release date {date}"
                                                id="productstate_preorder"
                                                values={{
                                                    date: formattedDate(
                                                        new Date(product.inventory.inStockDate)
                                                    )
                                                }}
                                            />
                                        )}
                                </Text>
                                {Boolean(isLowStockWarning) && (
                                    <Text fontWeight={'extrabold'} lineHeight={'105%'} pb={'2'}>
                                        <FormattedMessage
                                            defaultMessage="Last chance — only a few left"
                                            id="pdp_lowstock_warning"
                                        />
                                    </Text>
                                )}
                                {!cookieObj && customer?.isGuest && (
                                    <Text fontSize={'sm'} lineHeight={'17px'} pb={'2px'}>
                                        <FormattedMessage
                                            defaultMessage="Members save up to 15%"
                                            id="members_save"
                                        />
                                        <Link
                                            onClick={() => onSocialLoginClick()}
                                            textDecoration="underline"
                                            fontWeight="bold"
                                            href="#"
                                            marginLeft={1}
                                        >
                                            Sign Up or Log In
                                        </Link>
                                    </Text>
                                )}
                            </Box>
                            {typeof window !== 'undefined' && sezzleMerchantID && (
                                <SezzleWidget
                                    price={priceData?.currentPrice + ''}
                                    merchantId={sezzleMerchantID}
                                />
                            )}
                        </Box>
                    )}
                </Skeleton>
            )}
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    category: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    brandListCustomObj: PropTypes.object,
    sashes: PropTypes.array,
    customer: PropTypes.object,
    onSocialLoginClick: PropTypes.func,
    ssoEnable: PropTypes.bool
}

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */
const ProductView = forwardRef(
    (
        {
            product,
            category,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            updateWishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            setChildProductOrderability,
            bundleChildProducts,
            isBasketLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
            ...props
        },
        ref
    ) => {
        const {locale, site} = useMultiSite()
        const {sashes} = useProductSashes(product)
        const {currency: activeCurrency} = useCurrency()
        const {data: customer} = useCurrentCustomer()
        const {data: customerGroups} = useCustomerGroups(customer?.isGuest)
        const {data: basket} = useCurrentBasket()
        const customerGroupList = customer?.c_customerGroups || customerGroups?.result
        const configs = getConfig()
        const isMobileAppRestrictedSrc =
            configs?.app?.login?.social?.mobileAppRestricted || 'mobileapp'
        const isMobileAppRestricted =
            (product?.c_subscriptionProduct &&
                typeof window !== 'undefined' &&
                localStorage.getItem('sourceCode') === isMobileAppRestrictedSrc) ||
            false
        let isMember = true
        const {openMiniCartOnAddToCart} = useMiniCartContext()
        if (
            !isMobileAppRestricted &&
            customerGroupList?.length > 0 &&
            product?.c_customerGroups?.length > 0
        ) {
            isMember = false
            product?.c_customerGroups?.forEach((customerGroup) => {
                if (customerGroupList?.indexOf(customerGroup) > -1) {
                    isMember = true
                }
            })
        }
        if (
            !isMobileAppRestricted &&
            customerGroupList?.length > 0 &&
            product?.type?.bundle &&
            product?.bundledProducts?.length > 0
        ) {
            const restrictedChildFound = product.bundledProducts.some((child) => {
                const idEligible = isCustomerInProductGroups(
                    child?.product?.c_customerGroups,
                    customerGroupList
                )
                console.log('-----------' + idEligible)
                return !idEligible
            })
            if (restrictedChildFound) {
                isMember = false
            }
        }

        const isMobile = useBreakpointValue({base: true, sm: false, md: false})
        const isDesktop = useBreakpointValue({base: false, md: true})
        const showToast = useToast()
        const intl = useIntl()
        const {formatMessage} = useIntl()
        const productStyles = useMultiStyleConfig('ProductView')
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            stepQuantity,
            isOutOfStock,
            unfulfillable
        } = useDerivedProduct(product, isProductPartOfSet)
        const priceData = useMemo(() => {
            return getPriceData(product, activeCurrency)
        }, [product, quantity])
        const {data: wishlist} = useWishList()
        const {addItemToWishlist, removeItemFromWishlist} = useWishlistActions()
        const canAddToWishlist = !isProductLoading
        const isProductASet = product?.type?.set
        const isProductABundle = product?.type?.bundle
        const errorContainerRef = useRef(null)
        const contentAssetIds = ['Shipping-Delays-Returns']
        const contentAssets = useContentAsset(contentAssetIds, locale?.id)
        const shippingDelayReturnsContent = useMemo(() => {
            return contentAssets?.['Shipping-Delays-Returns']?.body || ''
        }, [contentAssets])
        // this will not render in DOM, the other isMobile does
        const useisMobile = useIsMobile(1023)
        const isPLPPage =
            typeof window !== 'undefined' && window.location.pathname.includes('/collections')
        const isFavourite = customer?.isRegistered
            ? wishlist?.registeredUserWishlists?.some((list) =>
                  list?.customerProductListItems?.some(
                      (item) => item?.productId === (product?.id || product?.productId)
                  )
              )
            : wishlist?.productWishList[0]?.customerProductListItems?.some(
                  (item) => item?.productId === (product?.id || product?.productId)
              )
        /** Stripe GooglePay/ApplePay Express Payment Request Buttons **/
        const {getcustomPreferencevalue} = useSiteCustomPreferences()
        const maxOrderQuantity = getMaxOrderQuantity(product, configs)
        const paymentProvider = getcustomPreferencevalue('pwaPaymentProvider')
        const [stripeEnabled, setStripeEnabled] = useState(false)
        const isPriceAvailable = Boolean(
            priceData.currentPrice > 0 ||
                priceData.listPrice > 0 ||
                priceData.maxPrice > 0 ||
                priceData.pricePerUnit > 0 ||
                priceData.tieredPrice > 0
        )
        const {data: brandListCustomObjRes, isSuccess} = useCustomObject({
            objectTypeId: 'BrandList',
            objectKey: product?.c_seriesNameIP
        })
        const [brandListCustomObj, setBrandListCustomObj] = useState(null)
        useEffect(() => {
            setBrandListCustomObj(brandListCustomObjRes)
        }, [
            brandListCustomObjRes?.response?.name,
            brandListCustomObjRes?.response?.bannerImage,
            brandListCustomObjRes?.response?.logoUrl,
            isSuccess,
            product?.c_seriesNameIP
        ])
        const {customInventoryMessage} = useMemo(() => {
            let currentInventoryMsg = ''
            if ((isProductASet || isProductABundle) && childProductOrderability) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    }
                )

                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Only {stockLevel} left for {productName}!',
                                id: 'use_product.message.inventory_remaining_for_product'
                            },
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName
                            }
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Out of stock for {productName}',
                                id: 'use_product.message.out_of_stock_for_product'
                            },
                            {productName: unavailableChildProduct.productName}
                        )
                    }
                }
            }
            return {customInventoryMessage: currentInventoryMsg}
        }, [showInventoryMessage, childProductOrderability])

        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(
                variant,
                quantity,
                product?.inventory?.ats
            )
            const showError = !isProductASet && !isProductABundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView

            toggleShowOptionsMessage(showError)

            if (scrollToError) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }

            return hasValidSelection
        }

        const isExpressPayment =
            stripeEnabled &&
            isPriceAvailable &&
            !product?.c_subscriptionProduct &&
            product?.inventory?.stockLevel > 0 &&
            !(product?.inventory?.backorderable && product?.inventory?.stockLevel <= 0) &&
            !(product?.inventory?.preorderable && product?.inventory?.stockLevel <= 0) &&
            variant &&
            !isProductABundle

        const [isGpayloaded, setGpayloaded] = useState(false) // Controls layout
        const [isAddingToCart, setIsAddingToCart] = useState(false)

        // Callback to receive button status from nested components
        const handleExpressPaymentStatusChange = useCallback((isButtonLoaded) => {
            setGpayloaded(isButtonLoaded)
        }, [])

        /* switch between layout onclick of carousel tiles
        Only reset if the component shouldn't render GPay */
        useEffect(() => {
            if (!isExpressPayment && isGpayloaded) {
                setGpayloaded(false)
            }
        }, [isExpressPayment, isGpayloaded])

        const renderActionButtons = () => {
            const buttons = []
            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update'
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Cart',
                    id: 'product_view.button.add_to_cart'
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart'
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add to Cart',
                    id: 'product_view.button.add_all_to_cart'
                }),
                addToWishlist: intl.formatMessage({
                    defaultMessage: 'Add to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                }),
                removeFromWishlist: intl.formatMessage({
                    defaultMessage: 'Remove From Wishlist',
                    id: 'product_view.button.remove_from_wishlist'
                }),
                addSetToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Set to Wishlist',
                    id: 'product_view.button.add_set_to_wishlist'
                }),
                addBundleToWishlist: intl.formatMessage({
                    defaultMessage: 'Add to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                }),
                preorder: intl.formatMessage({
                    defaultMessage: 'Pre Order',
                    id: 'product_view.button.PreOrder'
                }),
                backorder: intl.formatMessage({
                    defaultMessage: 'Backorder',
                    id: 'product_view.button.Backorder'
                })
            }

            const handleCartItem = async () => {
                if (isAddingToCart) return
                setIsAddingToCart(true)
                try {
                    const hasValidSelection = validateAndShowError()
                    if (!hasValidSelection) {
                        return null
                    }

                    const isAddToCartAllowed = canAddToCart(
                        product,
                        quantity,
                        basket,
                        maxOrderQuantity,
                        showToast,
                        formatMessage
                    )
                    if (!isAddToCartAllowed) {
                        return null
                    }

                    if (!addToCart && !updateCart) return null

                    if (updateCart) {
                        await updateCart(variant || product, quantity)
                        return
                    }

                    try {
                        await addToCart(variant, quantity)
                        showToast({
                            title: formatMessage(TOAST_MESSAGE_ADDED_TO_CART),
                            status: 'success',
                            isClosable: false,
                            duration: 3000
                        })
                        openMiniCartOnAddToCart()
                    } catch (e) {
                        showToast({
                            title: intl.formatMessage(API_ERROR_MESSAGE),
                            status: 'error'
                        })
                    }
                } finally {
                    setIsAddingToCart(false)
                }
            }

            // child product of bundles do not have add to cart button
            if (
                (addToCart || updateCart) &&
                !isProductPartOfBundle &&
                (!isOutOfStock || product?.inventory?.ats > 0)
            ) {
                buttons.push(
                    <Button
                        data-testid={'add-to-cart-button'}
                        key="cart-button"
                        onClick={handleCartItem}
                        disabled={isBasketLoading || showInventoryMessage || isMobileAppRestricted}
                        isDisabled={product?.inventory?.ats === 0 || !isPriceAvailable}
                        isLoading={isAddingToCart}
                        variant="primary"
                        w={isGpayloaded ? '100%' : {base: '100%', md: '85%', lg: '70.5%'}}
                        {...productStyles.atcButton}
                    >
                        {getCartButtonText({
                            updateCart,
                            isProductASet,
                            isProductABundle,
                            sashes,
                            buttonText
                        })}
                    </Button>
                )
            }

            if (isExpressPayment) {
                buttons.push(
                    <StripePDPExpressCta
                        product={product}
                        variant={variant}
                        quantity={quantity}
                        stripeEnabled={stripeEnabled}
                        priceData={priceData}
                        onExpressPaymentChange={handleExpressPaymentStatusChange}
                    />
                )
            }

            // child product of bundles do not have add to wishlist button
            if (
                (addItemToWishlist || updateWishlist) &&
                !isProductPartOfBundle &&
                !isMobileAppRestricted
            ) {
                const ariaLabel = updateWishlist
                    ? 'Update Wishlist'
                    : isProductASet
                    ? 'Add Set to Wishlist'
                    : isProductABundle
                    ? 'Add Bundle to Wishlist'
                    : isFavourite
                    ? 'Remove from Wishlist'
                    : 'Add to Wishlist'

                buttons.push(
                    <Box key="wishlist-button">
                        {customer?.isRegistered ? (
                            <WishlistDropdownButton
                                key="wishlist-button"
                                product={product}
                                variant={variant}
                                quantity={quantity}
                                isWishlistLoading={isWishlistLoading}
                                canAddToWishlist={canAddToWishlist}
                                ariaLabel={ariaLabel}
                                styles={productStyles.wishlistButton}
                                isMobile={isMobile}
                                isFavourite={isFavourite}
                                wishList={wishlist}
                            />
                        ) : (
                            <Button
                                className="wishlist-button"
                                onClick={handleWishlistItem}
                                disabled={isWishlistLoading || !canAddToWishlist}
                                isLoading={isWishlistLoading}
                                aria-label={ariaLabel}
                                variant="outline"
                                {...productStyles.wishlistButton}
                                tabIndex={0}
                            >
                                {isMobile ? (
                                    ariaLabel
                                ) : isFavourite ? (
                                    <HeartIconRed />
                                ) : (
                                    <HeartIconBlack />
                                )}
                            </Button>
                        )}
                    </Box>
                )
            }

            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        const {imageRef, descriptionRef, imageClassName, descriptionClassName} = useStickyScroll({
            enabled: !props.isQuickview && isDesktop // Only enable on desktop PDP
        })

        useEffect(() => {
            if (!isProductASet && !isProductABundle) {
                toggleShowOptionsMessage(false)
            }
        }, [variant?.productId, variant?.orderable, quantity])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName: product?.name
                    }
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        /** check payment providers and set the state varaibles */
        useEffect(() => {
            if (paymentProvider === 'stripe') {
                setStripeEnabled(true)
            }
        }, [paymentProvider])

        useCarouselObserver(() => {
            carouselDataComponentCount()
        })
        const handleWishlistItem = async () => {
            if (!updateWishlist && !addItemToWishlist) return null
            if (updateWishlist) {
                updateWishlist(product, variant, quantity)
                return
            }
            const wishlistItem = wishlist?.productWishList[0]?.customerProductListItems?.find(
                (item) => item?.productId === product?.id
            )
            if (!wishlistItem) {
                addItemToWishlist(product, quantity, variant)
            } else {
                removeItemFromWishlist(product, quantity, variant)
            }
        }
        const streetDate =
            product?.c_streetDate && product?.c_streetDate.trim() !== ''
                ? new Date(product?.c_streetDate)
                : new Date()
        const isComingSoon = product?.inventory?.ats === 0 && streetDate > new Date()
        const premiumURL = customer?.isRegistered ? PREMIUM_URL : '/login'
        const hasMoreThanThreeImages = product?.imageGroups?.[0]?.images?.length > 3

        // sso-login
        const authorizeIDP = useAuthHelper(AuthHelpers.AuthorizeIDP)
        const appOrigin = useAppOrigin()
        const redirectPath = configs?.app?.login?.social?.redirectURI || ''
        const redirectURI = buildRedirectURI(appOrigin, redirectPath, site, locale)
        const ssoEnable = getcustomPreferencevalue('pwaSSOEnable')
        const onSocialLoginClick = async () => {
            try {
                // Save the path where the user logged in
                setSessionJSONItem('returnToPage', window.location.pathname)
                await authorizeIDP.mutateAsync({
                    hint: configs?.app?.login?.social?.ssoIDPName,
                    redirectURI: redirectURI
                })
            } catch (error) {
                console.error('Error in onSocialLoginClick:', error)
            }
        }

        return (
            <Flex
                sx={productView}
                direction={'column'}
                data-testid="product-view"
                className={isPLPPage ? 'quick-view-product-view' : ''}
                ref={ref}
            >
                {!props.isQuickview && (
                    <>
                        <Flex
                            data-testid="sf-product-list-breadcrumb"
                            {...productStyles.breadcrumbList}
                        >
                            {/* Breadcrumb */}
                            {category && (
                                <Skeleton isLoaded={category} minWidth={64} fontWeight={600}>
                                    <Breadcrumb categories={category?.parentCategoryTree} />
                                </Skeleton>
                            )}
                        </Flex>
                        {/* Previously Purchased Section */}
                        <LastPurchased customer={customer} product={product} />
                    </>
                )}

                <Box>
                    <Box
                        ref={imageRef}
                        className={`image-section ${imageClassName}`}
                        data-testid="product-image-section"
                        {...productStyles.productImageSection}
                        w={
                            props.isQuickview
                                ? {base: '100%', sm: '100%', md: '47.5%'}
                                : {
                                      base: '100%',
                                      sm: hasMoreThanThreeImages ? '104%' : '100%',
                                      md: '47.5%'
                                  }
                        }
                    >
                        {product ? (
                            <Box
                                pos={'relative'}
                                // 105% to overlap in iPad
                                w={
                                    props.isQuickview
                                        ? {base: '100%', sm: '100%', md: '100%'}
                                        : {
                                              base: '100%',
                                              sm: hasMoreThanThreeImages ? '105%' : '100%',
                                              md: '100%'
                                          }
                                }
                                overscroll={'scroll'}
                            >
                                <Box
                                    data-testid="wishlist-on-image"
                                    onClick={handleWishlistItem}
                                    {...productStyles.imageWishlistIcon}
                                    as="button"
                                    type="button"
                                    tabIndex={0}
                                    aria-pressed={isFavourite}
                                    aria-label={
                                        isFavourite ? 'Remove from wishlist' : 'Add to wishlist'
                                    }
                                >
                                    {isFavourite ? <HeartIconRed /> : <HeartIconBlack />}
                                </Box>

                                <ImageGallery
                                    size={imageSize}
                                    imageGroups={product.imageGroups}
                                    selectedVariationAttributes={variationParams}
                                    lazy={isProductPartOfSet}
                                    bundleChildProducts={bundleChildProducts}
                                    isProductABundle={isProductABundle}
                                    isQuickview={props.isQuickview}
                                />
                            </Box>
                        ) : (
                            <ImageGallerySkeleton />
                        )}
                    </Box>

                    {/* Glace section */}
                    <VStack
                        ref={descriptionRef}
                        className={`product-glance-section ${descriptionClassName}`}
                        align="stretch"
                        // maxHeight={props.isQuickview ? '90vh' : ''}
                        overflowY={props.isQuickview && !isMobile ? 'auto' : ''}
                        {...productStyles.productGlanceSection}
                    >
                        <ProductViewHeader
                            name={product?.name}
                            product={product}
                            priceData={priceData}
                            currency={site?.l10n?.defaultCurrency || product?.currency}
                            category={category}
                            isProductPartOfBundle={isProductPartOfBundle}
                            brandListCustomObj={brandListCustomObj}
                            locale={intl.locale}
                            customer={customer}
                            sashes={sashes}
                            onSocialLoginClick={onSocialLoginClick}
                            ssoEnable={ssoEnable}
                        />
                        {isMobileAppRestricted && (
                            <Box color={'#c00'} fontWeight="extrabold">
                                <FormattedMessage
                                    defaultMessage="Only for Users"
                                    id="product_view.only_for_users"
                                />
                            </Box>
                        )}
                        <VStack align="stretch" data-testiid="purchase-configuration">
                            {isProductPartOfBundle && (
                                <Box mt="4">
                                    <Text fontWeight="medium" fontSize="md" aria-label="price">
                                        <label fontSize="14px">
                                            {intl.formatMessage({
                                                defaultMessage: 'Quantity : ',
                                                id: 'product_view.label_quantity'
                                            })}
                                            : {childOfBundleQuantity}
                                        </label>
                                    </Text>
                                </Box>
                            )}
                            {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                            {!isMobileAppRestricted && (
                                <>
                                    {showLoading ? (
                                        <>
                                            {/* First Attribute Skeleton */}
                                            <Skeleton height={6} width={32} />
                                            <Skeleton height={20} width={64} />

                                            {/* Second Attribute Skeleton */}
                                            <Skeleton height={6} width={32} />
                                            <Skeleton height={20} width={64} />
                                        </>
                                    ) : (
                                        variationAttributes.map(
                                            ({id, name, selectedValue, values}) => {
                                                const swatches = values.map(
                                                    (
                                                        {href, name, image, value, orderable},
                                                        index
                                                    ) => {
                                                        const content = image ? (
                                                            <Box
                                                                height="100%"
                                                                width="100%"
                                                                minWidth="32px"
                                                                backgroundRepeat="no-repeat"
                                                                backgroundSize="cover"
                                                                backgroundColor={name.toLowerCase()}
                                                                backgroundImage={`url(${
                                                                    image.disBaseLink || image.link
                                                                })`}
                                                            />
                                                        ) : (
                                                            name
                                                        )
                                                        const hasSelection = Boolean(
                                                            selectedValue?.value
                                                        )
                                                        const isSelected =
                                                            selectedValue?.value === value
                                                        const isFirst = index === 0
                                                        // To mimic the behavior of a native radio input, only
                                                        // one swatch should receive tab focus; the rest can be
                                                        // selected using arrow keys when the swatch group has
                                                        // focus. The focused element is the selected option or
                                                        // the first in the group, if no option is selected.
                                                        // This is a slight difference, for simplicity, from the
                                                        // native element, where the first element is focused on
                                                        // `Tab` and the _last_ element is focused on `Shift+Tab`
                                                        const isFocusable =
                                                            isSelected || (!hasSelection && isFirst)
                                                        return (
                                                            <Swatch
                                                                key={value}
                                                                href={href}
                                                                disabled={!orderable}
                                                                value={value}
                                                                name={name}
                                                                variant={
                                                                    id === 'color'
                                                                        ? 'circle'
                                                                        : 'square'
                                                                }
                                                                selected={isSelected}
                                                                isFocusable={isFocusable}
                                                            >
                                                                {content}
                                                            </Swatch>
                                                        )
                                                    }
                                                )
                                                return (
                                                    <SwatchGroup
                                                        key={id}
                                                        value={selectedValue?.value}
                                                        displayName={selectedValue?.name || ''}
                                                        label={intl.formatMessage(
                                                            {
                                                                defaultMessage: '{variantType}',
                                                                id: 'product_view.label.variant_type'
                                                            },
                                                            {variantType: name}
                                                        )}
                                                        onClick={() =>
                                                            toggleShowOptionsMessage(false)
                                                        }
                                                    >
                                                        {swatches}
                                                    </SwatchGroup>
                                                )
                                            }
                                        )
                                    )}
                                </>
                            )}
                            {/* Quantity Selector */}
                            {!isComingSoon &&
                                isMember &&
                                (!isOutOfStock || product?.inventory?.ats > 0) && (
                                    <HStack
                                        spacing={2}
                                        mt="4"
                                        alignItems={'baseline'}
                                        className={`purchase-layout ${
                                            isGpayloaded ? 'express-layout' : 'standard-layout'
                                        }`}
                                        flexDirection={
                                            isGpayloaded ? 'column' : {base: 'column', sm: 'row'}
                                        }
                                    >
                                        {!isMobileAppRestricted && (
                                            <QuantityPicker
                                                id="quantity"
                                                step={stepQuantity}
                                                value={quantity}
                                                min={minOrderQuantity}
                                                maxOrderQuantity={Number(maxOrderQuantity)}
                                                onChange={(stringValue, numberValue) => {
                                                    // Set the Quantity of product to value of input if value number
                                                    if (numberValue >= 0) {
                                                        setQuantity(numberValue)
                                                        if (isProductABundle) {
                                                            setSelectedBundleQuantity(numberValue)
                                                        }
                                                    } else if (stringValue === '') {
                                                        // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                        // User will not be able to add '' qauntity to the cart due to the add to cart button enablement rules
                                                        setQuantity(stringValue)
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    const value = e.target.value
                                                    if (parseInt(value) < 0 || value === '') {
                                                        setQuantity(minOrderQuantity)
                                                        if (isProductABundle) {
                                                            setSelectedBundleQuantity(
                                                                minOrderQuantity
                                                            )
                                                        }
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
                                                    // new quantity with one click. NOTE: This is something that can be refactored into the parent
                                                    // component, potentially as a prop called `selectInputOnFocus`.
                                                    e.target.select()
                                                }}
                                                productName={product?.name}
                                            />
                                        )}

                                        <Flex
                                            gap="2"
                                            flex={'1 1 80%'}
                                            flexDirection={{base: 'column', sm: 'row'}}
                                            w={isGpayloaded ? '100%' : {base: '100%', sm: 'auto'}}
                                        >
                                            {renderActionButtons()}
                                        </Flex>
                                    </HStack>
                                )}
                            {!isMember && (
                                <HStack
                                    spacing={2}
                                    flexDirection={{base: 'column', sm: 'row'}}
                                    width="100%"
                                    mt={6}
                                >
                                    <Button
                                        variant="secondary"
                                        isDisabled={true}
                                        color={'#8e8f9f'}
                                        width="100%"
                                        height="11"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Member Exclusive"
                                            id="pdp_member_exclusive"
                                        />
                                    </Button>
                                    <Button
                                        as="a"
                                        href={
                                            !customer?.isRegistered && ssoEnable ? '#' : premiumURL
                                        }
                                        onClick={() => {
                                            if (ssoEnable) onSocialLoginClick()
                                        }}
                                        variant="outline"
                                        width="100%"
                                        height="11"
                                        asChild
                                    >
                                        {customer?.isGuest ? (
                                            <FormattedMessage
                                                defaultMessage="Get Premium or Log In"
                                                id="pdp_get_premium"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Upgrade"
                                                id="pdp_upgrade"
                                            />
                                        )}
                                    </Button>
                                </HStack>
                            )}
                            <Box ref={errorContainerRef} data-testid="error-container">
                                {!showLoading && showOptionsMessage && (
                                    <Fade in={true}>
                                        <Text
                                            color="orange.600"
                                            fontWeight={600}
                                            textAlign={{base: 'center', sm: 'left'}}
                                        >
                                            {intl.formatMessage({
                                                defaultMessage:
                                                    'Please select all your options above'
                                            })}
                                        </Text>
                                    </Fade>
                                )}
                            </Box>

                            <Box className="notify-me product-unavilable" data-testid="notify-me">
                                <Box>
                                    {!showLoading && customInventoryMessage && !isComingSoon && (
                                        <Fade in={true}>
                                            <Text
                                                color="orange.600"
                                                fontWeight={600}
                                                marginBottom={8}
                                            >
                                                {customInventoryMessage}
                                            </Text>
                                        </Fade>
                                    )}
                                    {!showLoading &&
                                        (!product?.inventory?.ats === 0 || isOutOfStock) && (
                                            <Flex>
                                                <Box flex="1">
                                                    <BackInStock
                                                        product={product}
                                                        isComingSoon={isComingSoon}
                                                        onWishlistClick={handleWishlistItem}
                                                        isWishlistLoading={isWishlistLoading}
                                                        canAddToWishlist={canAddToWishlist}
                                                        productStyles={productStyles}
                                                        isFavourite={isFavourite}
                                                        isMobile={isMobile}
                                                    />
                                                </Box>
                                            </Flex>
                                        )}
                                </Box>
                            </Box>
                            {!useisMobile && !props.isQuickview && (
                                <ProductDescription product={product} />
                            )}

                            {!props.isQuickview && (
                                <>
                                    <SocialSharingIcons product={product} />
                                    <PremiumBanner
                                        premiumURL={PREMIUM_URL}
                                        onSocialLoginClick={onSocialLoginClick}
                                        customer={customer}
                                        ssoEnable={ssoEnable}
                                    />
                                </>
                            )}
                        </VStack>
                    </VStack>
                </Box>
                {!props.isQuickview && (
                    <>
                        <BundleChildCarousel bundleChildProducts={bundleChildProducts} />
                        {useisMobile && <ProductDescription product={product} />}{' '}
                        <ProductFeature product={product} />
                        <InformationAccordion
                            product={product}
                            shippingDelayReturnsContent={shippingDelayReturnsContent}
                        />
                        {/* {product?.c_seriesNameIP && (
                            <SubscribeToSeries
                                product={product}
                                brandListCustomObj={brandListCustomObj}
                            />
                        )} */}
                        <ReviewsAccordion product={product} />
                    </>
                )}
            </Flex>
        )
    }
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    bundleChildProducts: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isQuickview: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    isAddingToCart: PropTypes.bool,
    isItemAddedtToWishlist: PropTypes.bool,
    addToWishlist: PropTypes.func,
    removeFromWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number
}
export default ProductView
